import React, { useRef, useState } from "react";
import { Upload, X, Eye, RefreshCw, File as FileIcon } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Reusable admin upload field.
 * Props:
 *   value: string (URL) OR array of strings (for gallery=true)
 *   onChange: (newValue) => void
 *   accept: "image" | "pdf" | "image-pdf"
 *   gallery: boolean — if true, allows multiple uploads and stores an array
 *   label: string (optional label rendered above)
 *   fit: "cover" (photos, default) | "contain" (logos)
 */
export default function FileField({ value, onChange, accept = "image", gallery = false, label, fit = "cover" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const acceptAttr = accept === "pdf" ? "application/pdf,.pdf"
    : accept === "image-pdf" ? "image/*,application/pdf,.jpg,.jpeg,.png,.webp,.svg,.pdf"
    : "image/*,.jpg,.jpeg,.png,.webp,.svg";

  const items = gallery ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const uploadFiles = async (files) => {
    setErr(""); setBusy(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(r.data.url);
      }
      if (gallery) onChange([...(Array.isArray(value) ? value : []), ...urls]);
      else onChange(urls[0]);
    } catch (e) {
      setErr(String(e.response?.data?.detail || e.message || "Upload failed"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
  };

  const removeAt = (i) => {
    if (gallery) {
      const next = [...items]; next.splice(i, 1); onChange(next);
    } else onChange("");
  };

  const isPdf = (u = "") => u.toLowerCase().endsWith(".pdf");

  return (
    <div className="space-y-2">
      {label && <label className="text-xs uppercase tracking-widest text-slate-500 block">{label}</label>}
      {items.length > 0 && (
        <div className={`grid ${gallery ? "grid-cols-3 sm:grid-cols-5" : "grid-cols-1"} gap-2`}>
          {items.map((u, i) => (
            <div key={u + i} className="relative group border border-black/10 bg-slate-50" style={{ aspectRatio: gallery ? "1/1" : "16/9" }}>
              {isPdf(u) ? (
                <a href={u} target="_blank" rel="noreferrer" className="w-full h-full grid place-items-center text-slate-600 hover:text-pipeco-green">
                  <FileIcon size={28}/>
                  <span className="mt-1 text-[10px] uppercase tracking-widest">PDF</span>
                </a>
              ) : (
                <img src={u} alt="" className="w-full h-full" style={{ objectFit: fit }}/>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                <a href={u} target="_blank" rel="noreferrer" title="Preview" className="p-1.5 bg-white/90 hover:bg-white text-slate-800"><Eye size={13}/></a>
                <button type="button" onClick={()=>removeAt(i)} title="Remove" className="p-1.5 bg-white/90 hover:bg-red-500 hover:text-white text-slate-800"><X size={13}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={()=>inputRef.current?.click()}
          disabled={busy}
          data-testid="file-field-upload"
          className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-widest border border-pipeco-green text-pipeco-green hover:bg-pipeco-green hover:text-white transition-colors disabled:opacity-50"
        >
          {items.length > 0 && !gallery ? <RefreshCw size={13}/> : <Upload size={13}/>}
          {busy ? "Uploading…" : (items.length > 0 && !gallery ? "Replace" : gallery ? "Upload more" : "Upload")}
        </button>
        <span className="text-[10px] text-slate-500">
          {accept === "pdf" ? "PDF up to 25 MB" : accept === "image-pdf" ? "Image or PDF up to 25 MB" : "JPG/PNG/WEBP/SVG up to 25 MB · auto-optimised"}
        </span>
      </div>
      <input ref={inputRef} type="file" accept={acceptAttr} multiple={gallery} className="hidden" onChange={onPick}/>
      {err && <div className="text-red-600 text-xs">{err}</div>}
    </div>
  );
}
